<script lang="ts">
  import type { Pattern } from './pattern';
  import { getPatternById, patternTransformer, PatternTransformParams } from './pattern';
  import { Beat, Sample, CalculatedInfo } from './samples';
  import { beatInfoCollection, getBeatInfoById, sampleInfoCollection, getSampleInfoById } from './samples';

  import { beatInfo, sampleInfo } from './stores';    

  import Player from './Player/Player.svelte';
  import VolumeSettings from './Player/VolumeSettings.svelte';
  import SampleSelector from './Player/SampleSelector.svelte';
  import TTMChart from './TTMChart.svelte';
  import Turntable from './Player/Turntable.svelte';
  import SpeedSettings from './Player/SpeedSettings.svelte';
  import Transformations from './Player/Transformations.svelte';

  import { Button, Icon, Row, Col, ProgressLinear, Card, CardText } from 'svelte-materialify/src';
  import { mdiPlay, mdiStop, mdiEye, mdiEyeOff } from '@mdi/js';

  import { scale } from 'svelte/transition';
  import { quintOut } from 'svelte/easing';
  

  // props
  export let patternId: number;
  let beatId: string = beatInfoCollection.values().next().value.id; // TODO
  let sampleId: string = [...sampleInfoCollection][0][1].id; //sampleInfoCollection.values().next().value.id; // TODO

  let pattern: Pattern;
  let calculatedInfo: CalculatedInfo;

  let player: Player;

  let playbackRate: number = 1;

  let isInitialized: boolean = false;
  let isPlaying: boolean = false;
  let shouldPlay: boolean = false;
  let showTurntableView: boolean = false;

  let beat: Beat;
  let sample: Sample;  
  let patternTransformed: Pattern;
  let patternTransformParams: PatternTransformParams = new PatternTransformParams();

  const initialize = async (patternId: number): Promise<void> => {
    stopPlaying();
    isInitialized = false;
    if (!patternId) { return; }

    const patternOriginal = getPatternById(patternId);      
    patternTransformed = patternTransformer.transform(patternOriginal, patternTransformParams);      
    console.log('patternTransformed', patternTransformed, 'patternOriginal', patternOriginal);

    $sampleInfo = getSampleInfoById(sampleId);
    $beatInfo = getBeatInfoById(beatId);

    beat = new Beat($beatInfo);
    sample = new Sample($sampleInfo);
    await Promise.all([beat.init(), sample.init()]);
    console.log('sample', sample, 'beat', beat);

    recalculateInfo();
    
    await player.initialize(patternTransformed, beat, sample, calculatedInfo);

    pattern = patternTransformed;
    isInitialized = true;
  };

  const recalculateInfo = () => {
    calculatedInfo = new CalculatedInfo(patternTransformed, beat, playbackRate);
    console.log('calculatedInfo', calculatedInfo);
  };

  const reinitialize = async () => {
    await initialize(patternId);
    if (shouldPlay) {
      startPlaying();
    }
  };

  const resetTransformation = () => {
    patternTransformParams = new PatternTransformParams();
    reinitialize();
  };

  const setShouldPlay = () => {
    shouldPlay = true;
    startPlaying();
  }
  const setShouldNotPlay = () => {
    shouldPlay = false;
    stopPlaying();
  }

  const startPlaying = () => {
    player.startPlaying();
    isPlaying = true;
  };

  const restartPlaying = () => {
    stopPlaying();
    if (shouldPlay) {
      startPlaying();
    }
  };

  const stopPlaying = () => {
    if (!isPlaying) { return; }
    player.stopPlaying();
    isPlaying = false;
  };

  const changeSample = (_sampleId: string) => {
    sampleId = _sampleId;
    reinitialize();
  };

  const updatePlaybackRate = async (ev: CustomEvent) => {
    playbackRate = ev.detail;
    reinitialize();
  }

  const toggleTurntableView = () => {
    stopPlaying();
    showTurntableView = !showTurntableView;
  }

  const onResize = () => {
    stopPlaying();
  };

  // onMount(() => {
  //     window.addEventListener('resize', onResize);
  //     onResize();
  // });

  $: initialize(patternId);

</script>

<style>
.loader-box {
height: 20px; 
padding-top: 5px;
}
.turntable-card {
margin-top: 10px;
}
</style>


<!-- <Overlay active={!isInitialized} opacity={0.7} class="text-center">
<ProgressCircular indeterminate size={128} />
<div class="white-text">Loading pattern ..</div>
</Overlay> -->
<div class="loader-box">
{#if !isInitialized}
<ProgressLinear indeterminate color="secondary" />
{/if}
</div>

<div>

  <div style="margin-bottom: 10px;">
    <h5 class="text-h5">{pattern ? pattern.name : '---'}</h5>
  </div>

  <Row noGutters>
    <Col cols={4}>
        {#if isPlaying }
            <Button on:click={setShouldNotPlay} class="secondary-color black-text">
              <Icon path={mdiStop} />
              stop
            </Button>
        {:else}
            <Button on:click={setShouldPlay} class="secondary-color black-text">
              <Icon path={mdiPlay} />
              play
            </Button>
        {/if}
    </Col>
    <Col cols={8}>
      <div class="text-right">
        <Button on:click={toggleTurntableView}>
          <Icon path={showTurntableView ? mdiEyeOff : mdiEye} />
          &nbsp; Equipment
        </Button>
        <SampleSelector on:selectsample={(ev) => changeSample(ev.detail)} />
        <VolumeSettings />
      </div>
    </Col>
  </Row>

  {#if showTurntableView}
    <div class="turntable-card" transition:scale="{{duration: 500, delay: 250, opacity: 0.5, start: 0.6, easing: quintOut}}">
      <Card>
        <div class="pl-4 pr-4 pt-3">
            <span class="text-overline">Turntable & Mixer view</span>
            <!-- <br />
            <span class="text-h5 mb-2">Turntable & Mixer view</span>
            <br /> -->
        </div>
        <CardText>
            <Turntable {pattern} {isPlaying}
              durationSample={calculatedInfo?.durationForPattern} 
              {stopPlaying} />
        </CardText>
      </Card>
    </div>
  {/if}


  <Player bind:this={player} />

  <TTMChart {pattern} {isPlaying} {calculatedInfo} />

  <Transformations {patternTransformParams} 
    on:updatePatternTransformParams={reinitialize}
    on:resetPatternTransformParams={resetTransformation}
    />

  <SpeedSettings 
    on:updatePlaybackRate={updatePlaybackRate}
    {calculatedInfo} />


</div>